import BriefTeamList from '@/src/components/game/Leaderboard/BriefTeamList';
import ChallengeMetricsLeaderboard from '@/src/components/game/Leaderboard/ChallengeMetricsLeaderboard';
import { EventLog } from '@/src/components/game/Leaderboard/EventLog';
import { LeaderboardFooter } from '@/src/components/game/Leaderboard/LeaderboardFooter';
import PresenterModeTitle from '@/src/components/game/Leaderboard/PresenterModeTitle';
import TeamMetricsLeaderboard from '@/src/components/game/Leaderboard/TeamMetricsLeaderboard';
import { VideoModal } from '@/src/components/game/Leaderboard/VideoModal';
import Winners from '@/src/components/game/Leaderboard/Winners';
import { ScoringTrends } from '@/src/components/game/ScoringTrends/ScoringTrends';
import { config } from '@/src/config/app-config';
import { MAX_TIMEOUT_DELAY, ONE_MINUTE } from '@/src/constants/shared';
import { useEventsApi } from '@/src/hooks/api/useEventsApi';
import { useAudio } from '@/src/hooks/store/useAudio';
import { useVirtualMCSettings } from '@/src/hooks/store/useVirtualMCSettings';
import { useMouseActivity } from '@/src/hooks/useMouseActivity';
import { JAM_EVENT_DETAILS_ROUTES } from '@/src/routes';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { useJamLeaderboard } from '@/src/store/jam-leaderboard.context';
import { useJamMyTeam } from '@/src/store/jam-myteam.context';
import { useJamReport } from '@/src/store/jam-report.context';
import { useToolPanel } from '@/src/store/tool-panel.context';
import { LeaderboardEntry } from '@/src/types/JamReport';
import { VirtualMcVoiceType } from '@/src/types/VirtualMcVoiceType';
import { getCountdownAudio } from '@/src/utils/countdown.utils';
import { FeatureName, useFeatureUsageTracker } from '@/src/utils/engagement/engagement-tracker';
import { getErrorMessage } from '@/src/utils/errors.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import {
  Box,
  Button,
  ContentLayout,
  Header,
  Modal,
  Pagination,
  SpaceBetween,
  Toggle,
} from '@amzn/awsui-components-react';
import { sortBy } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Carousel from '@/src/components/ui/molecules/Carousel/Carousel';
import { Redirect, useHistory } from 'react-router-dom';
import Confetti from '../../ui/molecules/Confetti/Confetti';
import CountDown from '../../ui/molecules/CountDown/CountDown';
import TeamStatsInfo from '../../ui/molecules/MyJams/Leaderboard/TeamStatsInfo/TeamStatsInfo';
import SolvedChallengeMetrics from '../JamReport/SolvedChallengeMetrics';
import './Leaderboard.scss';

const pageSize = 15;
const DEFAULT_CAROUSEL_INTERVAL = 15000;

const Leaderboard = () => {
  const { t } = useTranslation();
  const {
    toggleShowToolPanel,
    toggleToolPanel,
    setToolPanelWidth,
    setHideAfterClose,
    showToolPanel,
    renderToolPanelContent,
  } = useToolPanel();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const { event, eventName, eventLogs, loadEventDetails, loadEventLogs } = useJamEventDetails();
  const [myTeamView, setMyTeamView] = useState(!event?.facilitator);
  const { myTeam: jamMyTeam } = useJamMyTeam();
  const {
    leaderboard,
    leaderboardTeamview,
    loadLeaderboardTeamview,
    loadLeaderboard,
    isPresenterMode,
    totalTeams,
    topTeams,
  } = useJamLeaderboard();
  const { addErrorFlashbar, clearFlashbars } = useFlashbars();
  const { audio, setAudio, isPlaying } = useAudio();
  const trackFeatureUsage = useFeatureUsageTracker();
  useMouseActivity();
  const history = useHistory();
  const { loadJamReport, reportApiData } = useJamReport();
  const [isLoading, setIsLoading] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [modalChallengeId, modalChallengeIdSet] = useState('');
  const [showOriginalTeamName, setShowOriginalTeamName] = useState(false);
  const [leaderboardHistory, setLeaderboardHistory] = useState<LeaderboardEntry[]>([]);
  const eventsApi = useEventsApi();
  const [autoplay, autoplaySet] = useState(true);
  const previousAutoplayPref = useRef(false);
  const isEnded = !!event?.ended;
  const showWinners = isEnded;
  const endDate = event?.eventEndDate;
  const myTeamId = jamMyTeam?.team.teamId || event?.teamId;

  const teams = useMemo(() => leaderboard || [], [leaderboard]);
  const sortedTeams = useMemo(() => sortBy(topTeams || [], ({ rank }) => rank), [topTeams]);

  const loadLeaderboardHistory = async () => {
    const response = await eventsApi.getLeaderboardHistory(eventName);
    setLeaderboardHistory(response);
  };

  const dataStore = useVirtualMCSettings();

  const fetchVideoSources = () => {
    dataStore.reset();
    if (!config.ff.virtualMcVideoSourcesUrl || !event?.virtualMcEnabled) {
      return;
    }

    fetch(config.ff.virtualMcVideoSourcesUrl)
      .then((r) => {
        if (r.ok) {
          return r.json().then((data) => {
            dataStore.setVideoSources(data);
          });
        } else {
          return r.text().then((data) => {
            // eslint-disable-next-line no-console
            console.error('error', data);
          });
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('error', e);
      });
  };

  useEffect(() => {
    fetchVideoSources();
  }, []);

  const latestMilestone = useRef<number | null>(null);
  const tryPlayVideo = () => {
    if (!leaderboard?.length || !event?.virtualMcEnabled) {
      return;
    }

    const teamWithMostChallengesCompleted = [...leaderboard].sort((a, b) => b.solvedChallenges - a.solvedChallenges)[0];

    // The first load is a baseline so milestones reached before the page opened don't replay.
    if (latestMilestone.current === null) {
      latestMilestone.current = teamWithMostChallengesCompleted?.solvedChallenges ?? 0;
      return;
    }

    const solvedChallenges = teamWithMostChallengesCompleted?.solvedChallenges;

    if (!solvedChallenges || solvedChallenges <= latestMilestone.current) {
      return;
    }

    if (!isPresenterMode || !dataStore.videoAutoplay) {
      latestMilestone.current = solvedChallenges;
      return;
    }

    const milestones = dataStore.videoSources.milestones ?? [];

    const videoSrc = (() => {
      const { challengeCompleted } = dataStore.videoSources;

      if (solvedChallenges > milestones.length) {
        return challengeCompleted;
      }

      // if they have completed all the challenges, play the last video
      if (solvedChallenges >= event?.challenges?.length) {
        return milestones[milestones.length - 1] || challengeCompleted;
      }

      return milestones[solvedChallenges - 1] || challengeCompleted;
    })();

    // Only record the milestone once the timer fires, so a leaderboard refresh that clears it reschedules the video.
    return setTimeout(
      () => {
        latestMilestone.current = solvedChallenges;

        if (videoSrc) {
          dataStore.setPlayingVideoSrc(videoSrc);
          dataStore.setVideoVisible(true);
        }
      },
      isPlaying ? 15000 : 0
    );
  };

  useEffect(() => {
    const timeoutId = tryPlayVideo();

    return () => clearTimeout(timeoutId);
  }, [leaderboard]);

  useEffect(() => {
    if (isPresenterMode) {
      void loadLeaderboardHistory();
    }
  }, [isPresenterMode]);

  /* start of showing/hiding team details panel logic */
  const hideToolPanel = () => {
    toggleShowToolPanel(false);
    toggleToolPanel(false);
    setHideAfterClose(false);
  };

  const setupToolPanel = () => {
    toggleShowToolPanel(true);
    toggleToolPanel(true);
    setToolPanelWidth(400);
    setHideAfterClose(true);
  };

  const showTeamDetails = (selectedTeamName: string | null) => {
    let team;
    if (
      !selectedTeamName ||
      !(team = [...teams, ...topTeams, ...leaderboardTeamview].find(({ teamName }) => teamName === selectedTeamName))
    ) {
      addErrorFlashbar(t(i18nKeys.general.error), t(i18nKeys.jamDashboard.leaderboard.error));
      return;
    }
    if (!showToolPanel) {
      setupToolPanel();
    }
    renderToolPanelContent(
      <TeamStatsInfo
        leaderboardInfo={team}
        teamId={team.teamName}
        onClose={hideToolPanel}
        eventName={eventName}
        rankingStrategy={event?.rankingStrategy}
      />
    );
  };
  /* end of showing/hiding team details panel logic */

  const getCarouselTitle = (index: number): string => {
    const titles = [t(i18nKeys.jamDashboard.leaderboard.title)];
    titles.push(t(i18nKeys.jamDashboard.leaderboard.scoringTrends));
    titles.push(t(i18nKeys.jamDashboard.leaderboard.chalengeStatistics));
    titles.push(t(i18nKeys.jamDashboard.leaderboard.eventLog.title));
    return titles[index];
  };

  const openChallengeMetricsModal = (challengeId: string) => {
    modalChallengeIdSet(challengeId);
    previousAutoplayPref.current = autoplay;
    autoplaySet(false);
  };

  const carouselItems = useMemo(() => {
    const childList = [];
    childList.push(
      <TeamMetricsLeaderboard
        key="team-metrics"
        index={0}
        showTeamDetails={showTeamDetails}
        showOriginalTeamName={showOriginalTeamName}
        leaderboard={leaderboard}
        isLoading={isLoading}
        numOfChallenges={event?.challenges?.length || 0}
        rankingStrategy={event?.rankingStrategy}
      />
    );
    if (event !== undefined) {
      childList.push(
        <ScoringTrends
          key="scoring-trends"
          leaderboardEntries={leaderboardHistory}
          eventStartDate={new Date(event.eventStartDate)}
        />
      );
    }

    childList.push(
      <ChallengeMetricsLeaderboard
        key="challenge-metrics"
        reportApiData={reportApiData}
        openChallengeMetricsModal={openChallengeMetricsModal}
        isLoading={isLoading}
      />
    );

    if (event !== undefined) {
      childList.push(
        <EventLog
          key="event-logs"
          event={event}
          eventLogs={eventLogs}
          isLoading={isLoading}
          showOriginalTeamName={showOriginalTeamName}
          teams={teams}
        />
      );
    }

    return childList;
  }, [
    config,
    event,
    isLoading,
    eventLogs,
    leaderboardHistory,
    showOriginalTeamName,
    leaderboard,
    showTeamDetails,
    reportApiData,
    openChallengeMetricsModal,
  ]);

  const getTopTeamList = () => {
    if (myTeamView) {
      return leaderboardTeamview;
    }
    return sortedTeams.slice(0, 3);
  };

  const navigateToReport = () => {
    history.push(JAM_EVENT_DETAILS_ROUTES.Report.resolve(eventName));
  };

  const loadLeaderboardList = async (pageIndex: number, showLoader = true) => {
    clearFlashbars();
    setIsLoading(showLoader);
    try {
      await loadLeaderboard(pageIndex + 1, pageSize);
    } catch (e: any) {
      addErrorFlashbar(getErrorMessage(e));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    void loadLeaderboardList(currentPageIndex);
  }, [currentPageIndex]);

  useEffect(() => {
    if (myTeamView && myTeamId) {
      void loadLeaderboardTeamview(myTeamId).catch((e: any) => addErrorFlashbar(getErrorMessage(e)));
    }
  }, [myTeamView, myTeamId]);

  const tryPlayClosingVideo = () => {
    if (!endDate || isEnded) {
      return;
    }

    const ms = moment(endDate).diff(moment(), 'milliseconds') - 1;

    // refresh event data once event is over so we can show winners
    if (ms <= MAX_TIMEOUT_DELAY) {
      return setTimeout(() => {
        void loadEventDetails(eventName, false);

        if (dataStore.videoAutoplay && dataStore.videoSources.closing) {
          dataStore.setPlayingVideoSrc(dataStore.videoSources.closing);
          dataStore.setVideoVisible(true);
        }
      }, ms);
    }
  };

  useEffect(() => {
    const timeoutId = tryPlayClosingVideo();

    return () => clearTimeout(timeoutId);
  }, [endDate, isEnded, eventName]);

  useEffect(() => {
    setCurrentPageIndex(0);
    if (isPresenterMode) {
      void loadJamReport(eventName);
      void loadEventLogs(eventName);
    }
  }, [isPresenterMode, eventName]);

  const interval = useRef(0);

  useEffect(() => {
    if (isPresenterMode) {
      interval.current = setInterval(() => {
        void loadLeaderboardList(currentPageIndex, false);
      }, 30000);
    } else {
      clearInterval(interval.current);
    }

    return () => clearInterval(interval.current);
  }, [isPresenterMode, currentPageIndex]);

  useEffect(() => {
    if (!isPresenterMode) {
      return;
    }
    if (carouselIndex === 0) {
      void loadLeaderboardList(0, false);
    } else if (carouselIndex === 1) {
      void loadLeaderboardHistory();
    } else if (carouselIndex === 2) {
      void loadJamReport(eventName);
    } else if (carouselIndex === 3) {
      void loadEventLogs(eventName);
    }
  }, [isPresenterMode, carouselIndex]);

  // Record exactly one view event each time the leaderboard enters presenter mode.
  useEffect(() => {
    if (isPresenterMode) {
      trackFeatureUsage(FeatureName.LEADERBOARD_PRESENTER_MODE, 'view');
    }
  }, [isPresenterMode]);

  useEffect(() => {
    if (!event?.facilitator) {
      return;
    }

    if (audio && isPresenterMode && dataStore.audioEnabled && !dataStore.videoVisible && !isPlaying) {
      // Virtual MC announcements describe leaderboard rank/score changes, which are only shown
      // on the first carousel page. Jump back to it so the announcement matches what's on screen.
      setCarouselIndex(0);
      audio.play();
      setAudio(undefined);
    }
  }, [audio, isPlaying, dataStore.videoVisible]);

  useEffect(() => {
    if (audio && !isPresenterMode) {
      audio.pause();
      setAudio(undefined);
    }
  }, [isPresenterMode]);

  const isTimestampWithin = (timestamp: number, msAgo: number) => {
    const now = Date.now();
    const timeAgo = now - msAgo;

    return timestamp >= timeAgo && timestamp <= now;
  };

  const checkCountdownAudio = () => {
    if (!event?.eventEndDate || !event?.virtualMcVoice) {
      return;
    }

    const endTimestamp = new Date(event?.eventEndDate).getTime();
    const voice = event.virtualMcVoice as VirtualMcVoiceType;

    if (isTimestampWithin(endTimestamp - 30 * ONE_MINUTE, 1000)) {
      setAudio(getCountdownAudio(voice, 30));
    }

    if (isTimestampWithin(endTimestamp - 20 * ONE_MINUTE, 1000)) {
      setAudio(getCountdownAudio(voice, 20));
    }

    if (isTimestampWithin(endTimestamp - 10 * ONE_MINUTE, 1000)) {
      setAudio(getCountdownAudio(voice, 10));
    }

    if (isTimestampWithin(endTimestamp - 5 * ONE_MINUTE, 1000)) {
      setAudio(getCountdownAudio(voice, 5));
    }

    if (isTimestampWithin(endTimestamp - 1 * ONE_MINUTE, 1000)) {
      setAudio(getCountdownAudio(voice, 1));
    }
  };

  useEffect(() => {
    if (!isPresenterMode) {
      return;
    }

    if (!config.ff.enableVirtualMc || !event?.eventEndDate || !event?.virtualMcEnabled || !event.virtualMcVoice) {
      return;
    }

    checkCountdownAudio();

    const intervalId = setInterval(checkCountdownAudio, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const dismissChallengeMetricsModal = () => {
    modalChallengeIdSet('');
    autoplaySet(previousAutoplayPref.current);
  };

  if (!event?.gamified && !event?.facilitator) {
    return <Redirect to={JAM_EVENT_DETAILS_ROUTES.Challenges.resolve(event?.eventName || '')} />;
  }

  return (
    <div className="leaderboard">
      <ContentLayout
        disableOverlap={showWinners || isPresenterMode}
        header={
          <div style={{ position: 'relative' }}>
            <Box margin={{ top: 'l' }}>
              <Header
                variant="h1"
                actions={
                  !isPresenterMode && (
                    <SpaceBetween direction="horizontal" size="s" alignItems="center">
                      {!event?.facilitator && (
                        <Toggle onChange={({ detail }) => setMyTeamView(detail.checked)} checked={myTeamView}>
                          {t(i18nKeys.jamDashboard.leaderboard.toggleButton)}
                        </Toggle>
                      )}
                      {event?.facilitator && (
                        <Toggle
                          data-testid="show-original-team-name-toggle"
                          onChange={({ detail }) => setShowOriginalTeamName(detail.checked)}
                          checked={showOriginalTeamName}
                        >
                          {t(i18nKeys.jamDashboard.leaderboard.toggleOriginalTeamName)}
                        </Toggle>
                      )}
                      {!isEnded && (
                        <div className="report-button">
                          <Button
                            disabled={isLoading}
                            variant="primary"
                            onClick={() => void loadLeaderboardList(currentPageIndex)}
                          >
                            {t(i18nKeys.facilitator.participants.buttons.refresh)}
                          </Button>
                        </div>
                      )}
                      {isEnded && (
                        <div className="report-button">
                          <Button variant="primary" onClick={navigateToReport}>
                            {t(i18nKeys.jamDashboard.leaderboard.viewTeamReport)}
                          </Button>
                        </div>
                      )}
                    </SpaceBetween>
                  )
                }
              />
              {showWinners && <Winners sortedTeams={sortedTeams} />}
              {isPresenterMode ? (
                <PresenterModeTitle
                  event={event}
                  getCarouselTitle={getCarouselTitle}
                  carouselIndex={0}
                  showWinners={showWinners}
                  endDate={endDate}
                />
              ) : (
                t(i18nKeys.jamDashboard.leaderboard.title)
              )}
            </Box>
            {showWinners && <Confetti />}
          </div>
        }
      >
        <div className="leaderboard__content">
          <SpaceBetween size="s" direction="vertical">
            {!showWinners && !isPresenterMode && (
              <>
                <div className="row">
                  <div className="box flexed center timer">
                    <Box variant="h1">{t(i18nKeys.jamDashboard.leaderboard.timeLeft)}: &nbsp;</Box>
                    <CountDown targetDate={endDate ? moment(endDate).valueOf() : 0} />
                  </div>
                </div>
                <BriefTeamList getTopTeamList={getTopTeamList} event={event} showTeamDetails={showTeamDetails} />
              </>
            )}
            <>
              {isPresenterMode ? (
                <Carousel
                  animation="slide"
                  interval={DEFAULT_CAROUSEL_INTERVAL}
                  // Pause auto-advance while a Virtual MC announcement is playing so the leaderboard
                  // stays on the first page (team ranks/scores) until the announcement finishes.
                  autoPlay={autoplay && !isPlaying}
                  selectedItem={carouselIndex}
                  onChange={setCarouselIndex}
                  infiniteLoop
                >
                  {carouselItems}
                </Carousel>
              ) : (
                <TeamMetricsLeaderboard
                  index={0}
                  showTeamDetails={showTeamDetails}
                  showOriginalTeamName={showOriginalTeamName}
                  leaderboard={leaderboard}
                  isLoading={isLoading}
                  numOfChallenges={event?.challenges?.length || 0}
                  rankingStrategy={event?.rankingStrategy}
                />
              )}
              {!isPresenterMode && totalTeams > 1 && (
                <div className="row center">
                  <div className="box pagination">
                    <Pagination
                      currentPageIndex={currentPageIndex + 1}
                      onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex - 1)}
                      pagesCount={totalTeams}
                      ariaLabels={{
                        nextPageLabel: t(i18nKeys.challenges.nextPageLabel),
                        paginationLabel: t(i18nKeys.challenges.paginationLabel),
                        previousPageLabel: t(i18nKeys.challenges.previousPageLabel),
                        pageLabel: (pageNumber) => `page ${pageNumber}`,
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          </SpaceBetween>
        </div>
        <Modal
          size="large"
          visible={!!modalChallengeId}
          onDismiss={dismissChallengeMetricsModal}
          header={<Header>{modalChallengeId}</Header>}
        >
          <SolvedChallengeMetrics challengeId={modalChallengeId} eventId={eventName} />
        </Modal>
        <VideoModal onClose={() => setCarouselIndex(0)} />
        <LeaderboardFooter
          carouselIndex={carouselIndex}
          onCarouselIndexChange={setCarouselIndex}
          autoplay={autoplay}
          onAutoplayChange={autoplaySet}
          pagesCount={carouselItems.length}
        />
      </ContentLayout>
    </div>
  );
};

export default Leaderboard;
